import { useMemo, useRef, useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import type { Session } from '@/api/client';
import { CloseIcon, DeleteIcon } from '@/components/ui/Icons';
import { IconButton } from '@/components/ui/IconButton/IconButton';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { useClickOutside } from '@/hooks/useClickOutside';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import { getStudioIndex } from '@/schedule/scheduleUtils';
import '@/components/ui/PopoverInlineLoading.css';
import '@/components/ProjectFilesPopover/ProjectFilesPopover.css';
import { SESSION_NO_PROJECT_LABEL, SESSION_READER_NOT_SET_LABEL } from '@/constants/sessionReader';
import './CanceledSessionsPopover.css';

function formatPopoverDayTitle(dateIso: string): string {
  const [y, m, d] = dateIso.split('-').map(Number);
  if (!y || !m || !d) return dateIso;
  const date = new Date(y, m - 1, d);
  const wd = [
    'Воскресенье',
    'Понедельник',
    'Вторник',
    'Среда',
    'Четверг',
    'Пятница',
    'Суббота',
  ][date.getDay()] ?? '';
  return `${wd}, ${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}`;
}

function studioLabel(session: Session): string {
  const m = String(session.studio ?? '').match(/\d+/);
  const n = m ? m[0] : String(getStudioIndex(session.studio) + 1);
  return `студия ${n}`;
}

export type CanceledSessionsPopoverProps = {
  open: boolean;
  onClose: () => void;
  dateIso: string;
  anchorEl: HTMLElement | null;
  sessions: Session[];
  onRestore: (session: Session) => Promise<void>;
  onDelete: (sessionId: string) => Promise<void>;
};

export type CanceledSessionsPopoverHandle = {
  /** Закрытие с анимацией; якорь держим до конца (useOverlayAnimation → onClose). */
  requestClose: () => void;
};

/**
 * Оболочка как ProjectFilesPopoverServer. Родитель монтирует только при открытии (`open={true}`),
 * чтобы при каждом открытии был новый mount и играла enter-анимация. Отдельная ветка `exiting` с
 * `open={false}` ломает useOverlayAnimation (hasBeenOpenRef на новом mount).
 */
export const CanceledSessionsPopover = forwardRef<CanceledSessionsPopoverHandle, CanceledSessionsPopoverProps>(
  function CanceledSessionsPopover(
    { open, onClose, dateIso, anchorEl, sessions, onRestore, onDelete },
    ref
  ) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { shouldRender, contentClasses, requestClose, isExiting } = useOverlayAnimation(open, onClose, true);

  useImperativeHandle(ref, () => ({ requestClose }), [requestClose]);

  const canceledForDay = useMemo(() => {
    const list = sessions.filter(
      (s) =>
        (s.date as string).slice(0, 10) === dateIso &&
        (s.is_canceled ?? false) &&
        (s.canceled_hidden_from_schedule ?? false)
    );
    return [...list].sort((a, b) =>
      (a.start as string).slice(0, 5).localeCompare((b.start as string).slice(0, 5))
    );
  }, [sessions, dateIso]);

  usePopoverPositioning({
    anchorEl,
    popoverRef,
    isOpen: shouldRender,
    preferredPositionX: 'left',
    preferredPositionY: 'bottom',
    /** Под кнопкой: правый край попапа = правый край якоря (открывается «вниз и влево»). */
    horizontalAlign: 'end',
    verticalAlign: 'center',
    offset: 8,
    margin: { top: 20, bottom: 20, left: 20, right: 20 },
    zIndex: 1000,
    priorityAxis: 'y',
    clampOnScrollFollow: true,
    onAnchorDetached: requestClose,
  });

  const anchorExcludeRef = useRef<HTMLElement | null>(null);
  anchorExcludeRef.current = anchorEl;

  useClickOutside(popoverRef, requestClose as (e: MouseEvent) => void, {
    excludeRefs: [anchorExcludeRef as React.RefObject<HTMLElement | null>],
    enabled: shouldRender,
    capture: false,
  });

  useEffect(() => {
    if (!shouldRender) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [shouldRender, requestClose]);

  const handleRestore = useCallback(
    async (s: Session) => {
      setRestoringId(s.id);
      try {
        await onRestore(s);
      } finally {
        setRestoringId(null);
      }
    },
    [onRestore]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      setDeletingId(id);
      try {
        await onDelete(id);
      } finally {
        setDeletingId(null);
      }
    },
    [onDelete]
  );

  const popoverContent = (
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        left: -9999,
        top: -9999,
        pointerEvents: isExiting ? 'none' : 'auto',
      }}
    >
      <OverlayIntrinsicSize>
        <div
          className={`project-files-popover canceled-sessions-popover ${contentClasses}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="canceled-sessions-popover-title"
        >
          <div className="project-files-popover__header">
            <h2 id="canceled-sessions-popover-title" className="project-files-popover__title">
              Отменённые сессии: {formatPopoverDayTitle(dateIso)}
            </h2>
            <IconButton
              aria-label="Закрыть"
              onClick={requestClose}
              size="m"
              iconSize={20}
              variant="danger"
              className="project-files-popover__close"
            >
              <CloseIcon />
            </IconButton>
          </div>
          <div className="project-files-popover__body">
            <OverlayScrollArea
              className="project-files-popover__scroll"
              osOptions={{ overflow: { x: 'visible', y: 'scroll' } }}
            >
              <div className="project-files-popover__scroll-pad">
                {canceledForDay.length === 0 ? (
                  <div className="project-files-popover__empty">
                    <p>Нет отменённых сессий на этот день</p>
                  </div>
                ) : (
                  <ul className="project-files-popover__list">
                    {canceledForDay.map((session) => {
                      const readerNames = session.readers?.length
                        ? session.readers.map((r) => r.name).join(', ')
                        : session.reader_name?.trim() || '';
                      const readerUnset = !readerNames && !session.reader_id;
                      const readerLabel =
                        readerNames ||
                        (session.reader_id ? 'Чтец' : SESSION_READER_NOT_SET_LABEL);
                      const project =
                        session.session_type === 'заметка'
                          ? (session.title?.trim() || 'Заметка')
                          : session.project_name?.trim() || SESSION_NO_PROJECT_LABEL;
                      const start = (session.start as string).slice(0, 5);
                      const end = (session.end as string).slice(0, 5);
                      const reason = session.canceled_note?.trim() || '—';
                      const busy = restoringId === session.id || deletingId === session.id;
                      return (
                        <li key={session.id} className="project-files-popover__item canceled-sessions-popover__item">
                          <div className="project-files-popover__item-body canceled-sessions-popover__item-body">
                            <div className="canceled-sessions-popover__item-content">
                              <span
                                className="canceled-sessions-popover__item-line1"
                                title={`${readerLabel} · ${project}`}
                              >
                                <span className={readerUnset ? 'canceled-sessions-popover__reader-unset' : undefined}>
                                  {readerLabel}
                                </span>
                                {' · '}
                                {project}
                              </span>
                              <div className="canceled-sessions-popover__item-meta">
                                С {start} до {end} · {studioLabel(session)}
                              </div>
                              <div className="canceled-sessions-popover__reason">
                                <span className="canceled-sessions-popover__reason-label">Причина:</span>{' '}
                                <span className="canceled-sessions-popover__reason-text">{reason}</span>
                              </div>
                            </div>
                            <div className="canceled-sessions-popover__item-actions-wrap">
                              <div className="project-files-popover__item-actions">
                                <IconButton
                                  aria-label="Восстановить сессию"
                                  size="s"
                                  variant="default"
                                  onClick={() => void handleRestore(session)}
                                  disabled={busy}
                                  title="Восстановить"
                                >
                                  <RestartAltIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                                <IconButton
                                  aria-label="Удалить сессию"
                                  size="s"
                                  variant="danger"
                                  onClick={() => void handleDelete(session.id)}
                                  disabled={busy}
                                  title="Удалить"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </div>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </OverlayScrollArea>
          </div>
        </div>
      </OverlayIntrinsicSize>
    </div>
  );

  return shouldRender ? createPortal(popoverContent, document.body) : null;
  }
);
